import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  UserCheck,
  Gift,
  FileText,
  BellOff,
  Trash2,
  AlertCircle,
  Calendar,
  CalendarX,
  Trophy,
  UserX,
  LogOut,
  CircleSlash,
  Sparkles,
  ArrowUpCircle,
  ArrowDownCircle,
  Clock,
  XCircle,
  RefreshCw,
  CreditCard,
  AlertTriangle,
  PauseCircle,
  TrendingUp,
  Ban,
  Loader2,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNotifications } from '@/hooks/useNotifications';
import { formatRelative } from '@/lib/dateUtils';
import type { Notification, NotificationType } from '@/types';
import { toast } from 'sonner';

const typeIcons: Record<NotificationType, { icon: typeof UserCheck; color: string }> = {
  // ── v1 — Application flow ─────────────────────────────────────────────────
  new_application: { icon: FileText, color: 'text-primary' },
  screening_completed: { icon: UserCheck, color: 'text-success' },
  interview_scheduled: { icon: Calendar, color: 'text-primary' },
  interview_cancelled: { icon: CalendarX, color: 'text-warning' },
  interview_report_ready: { icon: FileText, color: 'text-primary' },
  candidate_hired: { icon: Trophy, color: 'text-success' },
  candidate_rejected: { icon: UserX, color: 'text-warning' },
  candidate_withdrawn: { icon: LogOut, color: 'text-warning' },
  offer_declined: { icon: CircleSlash, color: 'text-warning' },
  pipeline_error: { icon: AlertCircle, color: 'text-destructive' },
  member_invited: { icon: Gift, color: 'text-warning' },
  // ── v2 — Trial nurturing ───────────────────────────────────────────────────
  trial_reminder: { icon: Sparkles, color: 'text-warning' },
  // ── v2 — Subscription lifecycle ────────────────────────────────────────────
  subscription_activated: { icon: ArrowUpCircle, color: 'text-success' },
  subscription_upgraded: { icon: ArrowUpCircle, color: 'text-success' },
  subscription_downgraded: { icon: ArrowDownCircle, color: 'text-warning' },
  subscription_cancellation_scheduled: { icon: Clock, color: 'text-warning' },
  subscription_canceled_effective: { icon: XCircle, color: 'text-warning' },
  subscription_uncanceled: { icon: RefreshCw, color: 'text-success' },
  // ── v2 — Payment / card / account ─────────────────────────────────────────
  payment_failed_retry: { icon: AlertTriangle, color: 'text-warning' },
  payment_failed_final: { icon: AlertTriangle, color: 'text-destructive' },
  card_expiring_soon: { icon: CreditCard, color: 'text-warning' },
  account_suspended_for_payment: { icon: PauseCircle, color: 'text-destructive' },
  // ── v2 — Cap warnings ──────────────────────────────────────────────────────
  cap_warning: { icon: TrendingUp, color: 'text-warning' },
  cap_reached: { icon: Ban, color: 'text-destructive' },
};

const defaultTypeIcon = { icon: FileText, color: 'text-primary' };

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NotificationCenter = ({ open, onOpenChange }: Props) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    notifications,
    unreadCount,
    refresh,
    markRead,
    markAllRead,
    deleteNotification,
    hasMore,
    loadingMore,
    loadMore,
  } = useNotifications();

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 120) {
      void loadMore();
    }
  }, [loadingMore, hasMore, loadMore]);

  const handleMarkAllRead = async () => {
    await markAllRead();
  };

  const handleDelete = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
      toast.success(t('notifications.dismissed'));
    } catch {
      toast.error(t('notifications.deleteFailed'));
    }
  };

  const handleClick = async (notification: Notification) => {
    if (!notification.read) {
      await markRead(notification.id);
    }
    if (notification.link) {
      onOpenChange(false);
      navigate(notification.link);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md p-0 bg-sidebar border-l border-sidebar-border flex flex-col h-full gap-0">
        <SheetHeader className="p-4 pb-3 border-b border-sidebar-border shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <SheetTitle className="text-sidebar-foreground">{t('notifications.title')}</SheetTitle>
              {notifications.length > 0 && (
                <p className="text-xs text-sidebar-foreground/50 mt-0.5">
                  {t('notifications.showingCount', { count: notifications.length })}
                  {unreadCount > 0 && (
                    <span className="ml-1">
                      · {t('notifications.unreadCount', { count: unreadCount })}
                    </span>
                  )}
                </p>
              )}
            </div>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent shrink-0 mr-8" onClick={() => { void handleMarkAllRead(); }}>
                {t('notifications.markAllRead')}
              </Button>
            )}
          </div>
        </SheetHeader>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        >
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-sidebar-foreground/50">
              <BellOff className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">{t('notifications.empty')}</p>
            </div>
          ) : (
            <>
              {notifications.map(n => {
                const { icon: Icon, color } = typeIcons[n.type] ?? defaultTypeIcon;
                return (
                  <div
                    key={n.id}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-sidebar-border/50 hover:bg-sidebar-accent/50 transition-colors cursor-pointer group ${!n.read ? 'bg-sidebar-accent/30' : ''}`}
                    onClick={() => { void handleClick(n); }}
                  >
                    {!n.read && <div className="h-2 w-2 rounded-full bg-primary mt-2 shrink-0" />}
                    {n.read && <div className="h-2 w-2 mt-2 shrink-0" />}
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm text-sidebar-foreground ${!n.read ? 'font-medium' : ''}`}>{n.title}</p>
                      <p className="text-xs text-sidebar-foreground/60 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-xs text-sidebar-foreground/40 mt-1">{formatRelative(n.createdAt)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-sidebar-foreground/50 hover:text-destructive hover:bg-sidebar-accent"
                      onClick={(e) => { void handleDelete(e, n.id); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
              {(loadingMore || hasMore) && (
                <div className="flex flex-col items-center justify-center gap-1 py-4 px-4 text-xs text-sidebar-foreground/50 border-t border-sidebar-border/50">
                  {loadingMore ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t('notifications.loadingMore')}
                    </>
                  ) : (
                    <>
                      <span>{t('notifications.showingCount', { count: notifications.length })}</span>
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => { void loadMore(); }}>
                        {t('notifications.loadMore')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default NotificationCenter;
